import React, { useState } from "react";
import { FiArrowLeft, FiSearch } from "react-icons/fi";

function ColorSearch({ onBack }) {
  const [selectedColor, setSelectedColor] = useState("#ff6b6b");
  const [searchResult, setSearchResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Convert hex to RGB
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? [
      parseInt(result[1], 16),
      parseInt(result[2], 16),
      parseInt(result[3], 16)
    ] : null;
  };

  // Convert RGB to LAB color space (accurate conversion)
  const rgbToLab = (color) => {
    // Normalize RGB values
    let r = color[0] / 255;
    let g = color[1] / 255;
    let b = color[2] / 255;

    // Convert sRGB to linear RGB (D65)
    r = r <= 0.04045 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4);
    g = g <= 0.04045 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4);
    b = b <= 0.04045 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4);

    // Convert to XYZ
    const X = r * 0.4124564 + g * 0.3575761 + b * 0.1804375;
    const Y = r * 0.2126729 + g * 0.7151522 + b * 0.072175;
    const Z = r * 0.0193339 + g * 0.119192 + b * 0.9503041;

    // D65 standard referent
    const ref_X = 0.95047;
    const ref_Y = 1.0;
    const ref_Z = 1.08883;

    // Normalize XYZ
    const x = X / ref_X;
    const y = Y / ref_Y;
    const z = Z / ref_Z;

    // Convert to LAB
    const fx = x > 0.008856 ? Math.pow(x, 1/3) : 7.787 * x + 16 / 116;
    const fy = y > 0.008856 ? Math.pow(y, 1/3) : 7.787 * y + 16 / 116;
    const fz = z > 0.008856 ? Math.pow(z, 1/3) : 7.787 * z + 16 / 116;

    const L = 116 * fy - 16;
    const A = 500 * (fx - fy);
    const B = 200 * (fy - fz);

    return { L, A, B }; // Return as object with L, A, B properties
  };

  const handleColorChange = (event) => {
    setSelectedColor(event.target.value);
    setError(null);
  };

  const handleSearch = async () => {
    setLoading(true);
    setError(null);

    try {
      // Convert hex to RGB
      const rgb = hexToRgb(selectedColor);
      if (!rgb) {
        throw new Error("Invalid color format");
      }

      // Convert RGB to LAB
      const lab = rgbToLab(rgb);

      // Send to Flask API
      const response = await fetch("http://127.0.0.1:5000/api", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          color: lab // Sending as object with L, A, B properties
        }),
      });

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      const result = await response.json();
      setSearchResult(result);
    } catch (error) {
      console.error("Error searching color:", error);
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        setError("Unable to connect to the server. Please make sure the Flask server is running.");
      } else {
        setError(error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  // Predefined color palette for quick selection
  const presetColors = [
    "#ff6b6b", "#4ecdc4", "#45b7d1", "#96ceb4", "#ffeaa7",
    "#fd79a8", "#fdcb6e", "#6c5ce7", "#74b9ff", "#00b894",
    "#e17055", "#81ecec", "#fab1a0", "#ff7675", "#a29bfe",
    "#fd79a8", "#fdcb6e", "#55a3ff", "#26de81", "#feca57",
    "#ff9ff3", "#54a0ff", "#5f27cd", "#00d2d3", "#ff9f43",
    "#ff6348", "#7bed9f", "#70a1ff", "#5352ed", "#dda0dd"
  ];

  return (
    <div className="search-container">
      <button className="back-button" onClick={onBack}>
        <FiArrowLeft />
      </button>
      <h2>Color Search</h2>

      <div className="color-search-form">
        <div className="color-picker-section">
          <h3 className="section-title">Select a Color</h3>
          
          <div className="color-picker-container">
            <div className="color-display">
              <div 
                className="selected-color-preview" 
                style={{ backgroundColor: selectedColor }}
              />
              <div className="color-info">
                <span className="color-hex">{selectedColor.toUpperCase()}</span>
                <span className="color-rgb">
                  {(() => {
                    const rgb = hexToRgb(selectedColor);
                    return rgb ? `RGB(${rgb[0]}, ${rgb[1]}, ${rgb[2]})` : '';
                  })()}
                </span>
              </div>
            </div>
            
            <input
              type="color"
              value={selectedColor}
              onChange={handleColorChange}
              className="color-input"
            />
          </div>

          <div className="preset-colors">
            <h4 className="preset-title">Quick Colors</h4>
            <div className="preset-grid">
              {presetColors.map((color, index) => (
                <button
                  key={index}
                  className={`preset-color ${selectedColor === color ? 'selected' : ''}`}
                  style={{ backgroundColor: color }}
                  onClick={() => setSelectedColor(color)}
                  title={color}
                />
              ))}
            </div>
          </div>

          <button
            className="search-button"
            onClick={handleSearch}
            disabled={loading}
          >
            <FiSearch />
            {loading ? "Searching..." : "Find Similar Colors"}
          </button>
        </div>

        {error && (
          <div className="error-message">
            <span>⚠️ {error}</span>
          </div>
        )}

        {searchResult && (
          <div className="search-results">
            <h3 className="section-title">Match Found</h3>
            <div className="result-card">
              <div className="result-colors">
                <div className="color-comparison">
                  <div className="comparison-item">
                    <div 
                      className="color-swatch" 
                      style={{ backgroundColor: selectedColor }}
                    />
                    <span className="color-label">Your Color</span>
                    <span className="color-hex">{selectedColor.toUpperCase()}</span>
                  </div>
                  <div className="comparison-arrow">→</div>
                  <div className="comparison-item">
                    <div 
                      className="color-swatch" 
                      style={{ backgroundColor: searchResult.hex }}
                    />
                    <span className="color-label">Best Match</span>
                    <span className="color-hex">{searchResult.hex.toUpperCase()}</span>
                  </div>
                </div>
              </div>
              
              <div className="result-details">
                <h4 className="result-name">{searchResult.name}</h4>
                <p className="result-brand">by {searchResult.brand}</p>
                {searchResult.image && (
                  <div className="result-swatch">
                    <img 
                      src={searchResult.image} 
                      alt={searchResult.name}
                      className="swatch-image"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ColorSearch;